import React from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

/**
 * Available MuiTypography heading variants (fontSize / lineHeight):
 *
 * - h1: 40px / 48px
 * - h2: 32px / 36px
 * - h3: 28px / 32px
 * - h4: 24px / 28px
 * - h5: 18px / 24px
 * - h6: 16px / 24px
 */

// Default heading levels to `h2`:
//
// This is so that you can drop `<Heading>` on a page _anywhere_ and it will be
// semantically correct. Since you should _usually_ only have 1 `h1` on the page,
// that can be done by explicitly overriding the `as` prop (`<Heading as="h1">`).
export const DEFAULT_HEADING_LEVEL = 2;
export const HeadingLevelContext = React.createContext<number | null>(null);

export interface HeadingProps
  extends Omit<TypographyProps, 'variant' | keyof PounceStyleProps>, PounceStyleProps {
  /** The typography header variants: h1, h2, h3, h4, h5, h6 */
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  /** Override the component type */
  as?: React.ElementType;
}

/**
 * Responsive typographic component. You should use that whenever you want to add a title to something.
 *
 * Using it inside an [Article](../?path=/docs/general-layout-article--docs) or a [Section](../?path=/docs/general-layout-section--docs)
 * component will cause an automatic inference of the heading level (h1, h2, h3, etc.)
 */
export const Heading = React.forwardRef<HTMLElement, HeadingProps>(function Heading(
  { variant, as, sx, children, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);
  const level = React.useContext(HeadingLevelContext);

  // `Math.min` makes sure that the heading level is never above a h6
  const hTag = `h${Math.min(level || DEFAULT_HEADING_LEVEL, 6)}` as React.ElementType;

  // Style variant defaults to h2
  const styleVariant = variant || 'h2';

  // Determine the component to render:
  // 1. `as` prop takes priority (explicit override)
  // 2. If variant is explicitly provided, align component to variant
  // 3. Otherwise, use context-based hTag (for Section nesting)
  const resolvedComponent = as || variant || hTag;

  // Combine pounce styles with user sx
  const combinedSx = {
    ...pounceStyles,
    ...(sx || {}),
  };

  return (
    <Typography
      ref={ref}
      variant={styleVariant}
      component={resolvedComponent}
      sx={combinedSx}
      {...otherProps}
    >
      {children}
    </Typography>
  );
});

export default Heading;
